import React, {
    useEffect
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { DialogInterface } from '../../utils/interfaces';
import Button from '../Button';
import MessageDialog from './MessageDialog';
import ActionDialog from './ActionDialog';
import UploadFileDialog from './UploadFileDialog';
import BetSlipDialog from './BetSlipDialog';
import CaseDialog from './CaseDialog';
import { useSessionService } from '../../hook/useSessionService';
import { useSocket } from '../../hook/useSocket';
import { useCacheService } from '../../hook/useCacheService';
import { useTranslation } from 'react-i18next';
import { removeDialog, addDialog } from '../../redux/state/dialog';
import { editStatus } from '../../redux/state/status';
import ButtonDialog from './ButtonDialog';
import Swal from 'sweetalert2';
import { getBrowser, getPlatform } from '../../utils/salesforceVisitorDetails';
import { editSession } from '../../redux/state/session';
import { useNavigate } from 'react-router-dom';
import { store } from '../../redux/store';
import { shouldShowWebToCaseMaintenance } from '../../utils/webToCaseMaintenance';

const Dialogs = () => {
    const navigate = useNavigate();
    const socket = useSocket();
    const cacheService = useCacheService();
    const botIdRequireLanguageSelection = [49, 75, '49', '75'];

    const { t } = useTranslation();

    const chatId = useSelector(
        (state: { session: { chatId: string } }) => state.session.chatId
    );

    const botId = useSelector(
        (state: { session: { botId: string } }) => state.session.botId
    );

    const username = useSelector(
        (state: { session: { username: string } }) => state.session.username
    );

    const nps = useSelector(
        (state: { session: { nps: string } }) => state.session.nps
    );

    const matchStartDate = useSelector(
        (state: { session: { matchStartDate: string } }) =>
            state.session.matchStartDate
    );

    const betslipId = useSelector(
        (state: { session: { betslipId: string } }) => state.session.betslipId
    );

    const queue = useSelector(
        (state: { session: { queue: string } }) => state.session.queue
    );

    const dialogs = useSelector(
        (state: { dialog: { dialogs: DialogInterface[] } }) =>
            state.dialog.dialogs
    );

    const botButtonColor = useSelector(
        (state: { settings: { botButtonColor: string } }) =>
            state.settings.botButtonColor
    );

    const botName = useSelector(
        (state: { settings: { botName: string } }) => state.settings.botName
    );

    const marketCode = useSelector(
        (state: { settings: { marketCode: string } }) => state.settings.marketCode
    );

    const enableOfflineForm = useSelector(
        (state: { settings: { enableOfflineForm: boolean } }) =>
            state.settings.enableOfflineForm
    );

    const useExternalLinkForOfflineForm = useSelector(
        (state: { settings: { useExternalLinkForOfflineForm: boolean } }) =>
            state.settings.useExternalLinkForOfflineForm
    );

    const externalLinkForOfflineForm = useSelector(
        (state: { settings: { externalLinkForOfflineForm: string } }) =>
            state.settings.externalLinkForOfflineForm
    );

    const forceClose = useSelector(
        (state: { systemMessages: { force_close: { message: string } } }) =>
            state.systemMessages.force_close.message
    );

    const noRespond = useSelector(
        (state: { systemMessages: { no_respond: { message: string } } }) =>
            state.systemMessages.no_respond.message
    );

    const leaveChat = useSelector(
        (state: { systemMessages: { leave_chat: { message: string } } }) =>
            state.systemMessages.leave_chat.message
    );

    const endChat = useSelector(
        (state: { systemMessages: { end_chat: { message: string } } }) =>
            state.systemMessages.end_chat.message
    );

    const networkProblem = useSelector(
        (state: { systemMessages: { network_problem: { message: string } } }) =>
            state.systemMessages.network_problem.message
    );

    const timeoutWarningMessage = useSelector(
        (state: {
            systemMessages: { timeout_warning_message: { message: string } };
        }) => state.systemMessages.timeout_warning_message.message
    );

    const quickReplyMessage = useSelector(
        (state: {
            systemMessages: { quickreplymessage: { message: string } };
        }) => state.systemMessages.quickreplymessage.message
    );

    const reconnectChat = useSelector(
        (state: { systemMessages: { reconnect_chat: { message: string } } }) =>
            state.systemMessages.reconnect_chat.message
    );

    const rejoinChat = useSelector(
        (state: { systemMessages: { rejoin_chat: { message: string } } }) =>
            state.systemMessages.rejoin_chat.message
    );

    const liveAgentInitFailMessage = useSelector(
        (state: {
            systemMessages: { live_agent_init_fail: { message: string } };
        }) => state.systemMessages.live_agent_init_fail.message
    );

    const leaveMessage = useSelector(
        (state: { systemMessages: { leave_message: { message: string } } }) =>
            state.systemMessages.leave_message.message
    );

    const uploadFile = useSelector(
        (state: {
            systemMessages: { upload_file_button: { message: string } };
        }) => state.systemMessages.upload_file_button.message
    );

    const chatMode = useSelector(
        (state: { session: { chatMode: string } }) => state.session.chatMode
    );

    const vip = useSelector(
        (state: { session: { isVip: any } }) => state.session.isVip
    );

    const refreshWhenBot = useSelector(
        (state: { session: { refreshWhenBot: boolean } }) =>
            state.session.refreshWhenBot
    );

    const laAffinityToken = useSelector(
        (state: { session: { laAffinityToken: string } }) =>
            state.session.laAffinityToken
    );

    const laSessionKey = useSelector(
        (state: { session: { laSessionKey: string } }) =>
            state.session.laSessionKey
    );

    const referrerSite = useSelector(
        (state: { session: { referrerSite: string } }) => state.session.referrerSite
    );

    const usernameFromUrl = useSelector(
        (state: { session: { usernameFromUrl: boolean } }) => state.session.usernameFromUrl
    );

    const customValues = useSelector(
        (state: { settings: { customValues: string } }) =>
            state.settings.customValues
    );

    const enQueue = useSelector(
        (state: { session: { enQueue: boolean } }) => state.session.enQueue
    );
    
    const sessionId = useSelector(
        (state: { session: { sessionId: string } }) => state.session.sessionId
    );

    const apiPath = useSelector(
        (state: { session: { apiPath: string } }) => state.session.apiPath
    );

    const caseNumber = useSelector(
        (state: { session: { caseNumber: string } }) => state.session.caseNumber
    );

    const liveChatValues = useSelector(
        (state: { settings: { botMarioConfig: { defaultValues: string } } }) =>
            state.settings.botMarioConfig.defaultValues
    );

    const isAuthenticated = useSelector(
        (state: { session: { isAuthenticated: any } }) => state.session.isAuthenticated
    );

    const brandCode = useSelector(
        (state: { settings: { brandCode: string } }) => state.settings.brandCode
    );

    const memberCode = useSelector(
        (state: { session: { memberCode: string } }) => state.session.memberCode
    );
    
    const chatRatingDone = useSelector(
        (state: { session: { chatRatingDone: boolean } }) => state.session.chatRatingDone
    );

    const dataCustomValues = customValues ? JSON.parse(customValues) : {};
    const webToCaseForm = dataCustomValues.hasOwnProperty('web_to_case_form') ? dataCustomValues.web_to_case_form : 'web_to_case';
    const isVipBot = String(dataCustomValues?.is_vip_bot ?? 'false').toLowerCase() === 'true';
    const isAffBot = String(dataCustomValues?.is_aff_bot ?? 'false').toLowerCase() === 'true';
    const endChatSettings = dataCustomValues.end_chat_settings ? JSON.parse(dataCustomValues.end_chat_settings) : {};
    const endChatPrompt = endChatSettings.end_chat_prompt ? endChatSettings.end_chat_prompt : null;
    const endChatYesButton = endChatSettings.yes_btn ? endChatSettings.yes_btn : null;
    const endChatNoButton = endChatSettings.no_btn ? endChatSettings.no_btn : null;

    const dispatch = useDispatch();

    const closeDialog = (type: string) => {
        dispatch(removeDialog(type));
    };

    const handleTimeoutWarningClick = () => {
        cacheService.refreshCache();
        closeDialog('timeoutWarning');
    };

    const handleReload = () => {
        window.location.reload();
    };

    const handleConnectAgent = () => {
        closeDialog('connectAgent');
        console.log('connect agent', botId, botIdRequireLanguageSelection, botIdRequireLanguageSelection.includes(botId));
        
        console.log("IS VIP BUTTON", vip, typeof vip, vip[0]);
        let parsedCustomValues = customValues ? JSON.parse(customValues) : {};
        if (parsedCustomValues['live_agent_mode'] && parsedCustomValues['live_agent_mode'] == '1') {
            dispatch(editSession({ name: 'chatMode', value: 'agent' }));
            cacheService.setCacheByField(sessionId, 'chatMode', 'agent');
            dispatch(editStatus({ name: 'queueStatus', value: 'queueLoading' }));
            cacheService.setCacheByField(sessionId, 'queueStatus', 'queueLoading');
            const agentAuthEnabled = String(parsedCustomValues?.agent_auth_enabled ?? 'true').toLowerCase() === 'true';
            socket.sendConnectAgentEvent({
                chatId: chatId,
                username: username,
                liveAgentFlag: parsedCustomValues['live_agent_mode'] ? parsedCustomValues['live_agent_mode'] : '1',
                isAuthenticated: isAuthenticated,
                isStartAgent: false,
                isStartBot: true,
                triggerTime: new Date().getTime().toString(),
                language: 'chinese',
                screen_resolution:
                        window.screen.width + 'x' + window.screen.height,
                userAgent: navigator.userAgent,
                nps: nps,
                matchStartDateTime: matchStartDate,
                betslipId: betslipId,
                type: 'connect',
                connect_flag: "CS Button Pressed",
                cacheId: botId.toString() + sessionId,
                cacheUrl : apiPath,
                browser: getBrowser(),
                memberCode: memberCode,
                brandCode: brandCode,
                marketCode: marketCode,
                agentAuthEnabled: agentAuthEnabled,
                isVipBot: isVipBot,
                botName: botName,
                isAffBot: isAffBot
            })
        } 
        else {
            if (vip[1] == "notChecked"){
                socket.sendCheckVipEvent({
                    chatId: chatId,
                    username: username,
                    usernameFromUrl: true, //check
                    recheckVip: true,
                    checkVipDetails: parsedCustomValues['check_vip_details'] ? parsedCustomValues['check_vip_details'] : null,
                    brandCode: brandCode
                })
    
            }
            if (botIdRequireLanguageSelection.includes(botId)) {
                Swal.fire({
                    text: t('Preferred Language'),
                    input: 'select',
                    inputOptions: {
                        en: 'English',
                        hindi: 'हिंदी',
                    },
                    inputPlaceholder: t('Please select your language'),
                    showCancelButton: false,
                    confirmButtonColor: botButtonColor,
                    allowOutsideClick: false,
                    confirmButtonText: t('Confirm'),
                    inputValidator: (value) => {
                        return new Promise((resolve: any) => {
                            if (value) {
                                resolve();
                            } else {
                                resolve(t('Please select your preferred language'));
                            }
                        });
                    },
                }).then((result) => {
                    if (result.value) {
                        dispatch(editSession({ name: 'chatMode', value: 'agent' }));
                        cacheService.setCacheByField(sessionId, 'chatMode', 'agent');
                        dispatch(
                            editStatus({ name: 'queueStatus', value: 'queueLoading' })
                        );
                        cacheService.setCacheByField(sessionId, 'queueStatus', 'queueLoading');

                        socket.sendConnectAgentEvent({
                            chatId: chatId,
                            isStartAgent: false,
                            isStartBot: true,
                            isVip: vip[0],
                            triggerTime: new Date().getTime().toString(),
                            language: 'chinese',
                            screen_resolution:
                                window.screen.width + 'x' + window.screen.height,
                            userAgent: navigator.userAgent,
                            nps: nps,
                            matchStartDateTime: matchStartDate,
                            betslipId: betslipId,
                            type: 'connect',
                            username: username,
                            queue: queue,
                            sfQueueId: result.value,
                            sfReferrer: referrerSite? referrerSite : document.referrer,
                            connect_flag: "CS Button Pressed",
                            cacheId: botId.toString() + sessionId,
                            cacheUrl : apiPath,
                            browser: getBrowser(),
                            sfPlatform: getPlatform(),
                            acqQueueFlag: parsedCustomValues.hasOwnProperty('acq_queue_flag') ? parsedCustomValues.acq_queue_flag : "",
                            enQueue: enQueue,
                            acqQueueDays: parsedCustomValues.hasOwnProperty('acq_queue_days') ? parsedCustomValues.acq_queue_days : "",
                            caseNumber: caseNumber,
                            memberCode: memberCode,
                            brandCode: brandCode,
                            marketCode: marketCode,
                            isAuthenticated: isAuthenticated,
                            isVipbot: isVipBot,
                            botName: botName,
                            isAffBot: isAffBot
                        });
                    }
                });
            } else {
                dispatch(editSession({ name: 'chatMode', value: 'agent' }));
                cacheService.setCacheByField(sessionId, 'chatMode', 'agent');
                dispatch(editStatus({ name: 'queueStatus', value: 'queueLoading' }));
                cacheService.setCacheByField(sessionId, 'queueStatus', 'queueLoading');
                socket.sendConnectAgentEvent({
                    chatId: chatId,
                    isStartAgent: false,
                    isStartBot: true,
                    isVip: vip[0],
                    triggerTime: new Date().getTime().toString(),
                    language: 'chinese',
                    screen_resolution:
                        window.screen.width + 'x' + window.screen.height,
                    userAgent: navigator.userAgent,
                    nps: nps,
                    matchStartDateTime: matchStartDate,
                    betslipId: betslipId,
                    type: 'connect',
                    username: username,
                    queue: queue,
                    sfReferrer: referrerSite? referrerSite : document.referrer,
                    connect_flag: "CS Button Pressed",
                    cacheId: botId.toString() + sessionId,
                    cacheUrl : apiPath,
                    browser: getBrowser(),
                    sfPlatform: getPlatform(),
                    acqQueueFlag: parsedCustomValues.hasOwnProperty('acq_queue_flag') ? parsedCustomValues.acq_queue_flag : "",
                    enQueue: enQueue,
                    acqQueueDays: parsedCustomValues.hasOwnProperty('acq_queue_days') ? parsedCustomValues.acq_queue_days : "",
                    caseNumber: caseNumber,
                    memberCode: memberCode,
                    brandCode: brandCode,
                    marketCode: marketCode,
                    isAuthenticated: isAuthenticated,
                    isVipBot: isVipBot,
                    botName: botName,
                    isAffBot: isAffBot
                });
            }
        }

        // console.log("refresh when bot", refreshWhenBot);

        // if (refreshWhenBot && laAffinityToken && laSessionKey) {
        //     console.log("refresh when bot triggered");
        //     socket.sendChatEndEvent({
        //         chatId: chatId,
        //         affinityToken: laAffinityToken,
        //         key: laSessionKey,
        //     });
        // }
    };

    const handleOfflineForm = () => {
        const sfAvailability = store.getState().settings.sfAvailability;
        const opensWebToCaseModalOrIframe =
            webToCaseForm !== 'chat_rating' ||
            chatRatingDone !== true;
        if (
            opensWebToCaseModalOrIframe &&
            shouldShowWebToCaseMaintenance(sfAvailability)
        ) {
            navigate('/maintenance', { replace: true });
            return;
        }
        if (webToCaseForm == "chat_rating"){
            if (chatRatingDone === true){
                Swal.fire({
                    title: '',
                    text: endChatPrompt? endChatPrompt : t('ratingSubmitted'),
                    icon: 'success',
                    confirmButtonText: endChatYesButton? endChatYesButton : t('queueProcessingDialog.close'),
                    confirmButtonColor: botButtonColor
                        ? botButtonColor.slice(0, 7)
                        : '',
                    showCancelButton: false,
                    cancelButtonText: endChatNoButton? endChatNoButton : t('ratingContinueChat'),
                }).then((result) => {
                    if (result.isConfirmed) {
                        dispatch(addDialog({ task: 'leaved', type: 'action' }));
                        cacheService.endSession(sessionId);
                    }
                });
            } else {
                dispatch(
                    editStatus({
                        name: 'isChatRatingModal',
                        value: true,
                    })
                );
            }
        } else{
            dispatch(
                editStatus({
                    name: 'isIframeModal',
                    value:
                        true && enableOfflineForm && useExternalLinkForOfflineForm,
                })
            );
            dispatch(
                editStatus({
                    name: 'iframeUrl',
                    value: externalLinkForOfflineForm,
                })
            );
        }
    };

    return (
        <div className="w-90pc medium:w-60pc absolute bottom-[12px] left-5pc medium:left-20pc text-sm z-10">
            {dialogs.map((dialog: DialogInterface, index) => {
                if (dialog.task == 'timeoutWarning') {
                    return (
                        <MessageDialog
                            color="grey"
                            message={timeoutWarningMessage}
                            key={index}
                            fn={handleTimeoutWarningClick}
                        />
                    );
                } else if (dialog.task == 'ended' && !isVipBot) {
                    return (
                        <ActionDialog
                            color="red"
                            message={endChat}
                            buttonMessage={rejoinChat}
                            key={index}
                            task={dialog.task}
                            fn={handleReload}
                        />
                    );
                } else if (dialog.task == 'leaved' && !isVipBot) {
                    return (
                        <ActionDialog
                            color="red"
                            message={leaveChat}
                            buttonMessage={rejoinChat}
                            key={index}
                            task={dialog.task}
                            fn={handleReload}
                        />
                    );
                } else if (dialog.task == 'disconnected' && !isVipBot) {
                    return (
                        <ActionDialog
                            color="red"
                            message={networkProblem}
                            buttonMessage={reconnectChat}
                            key={index}
                            task={dialog.task}
                            fn={handleReload}
                        />
                    );
                } else if (dialog.task == 'timeout' && !isVipBot) {
                    return (
                        <ActionDialog
                            color="red"
                            message={noRespond}
                            buttonMessage={reconnectChat}
                            key={index}
                            task={dialog.task}
                            fn={handleReload}
                        />
                    );
                } else if (
                    chatMode === 'bot' &&
                    dialog.task == 'connectAgent'
                ) {
                    // return (
                    //     <ActionDialog
                    //         color="blue"
                    //         // customColor={botButtonColor}
                    //         message={quickReplyMessage}
                    //         buttonMessage={t('connect')}
                    //         key={index}
                    //         task={dialog.task}
                    //         fn={handleConnectAgent}
                    //     />
                    // );
                    const defaultValues: { Default?: string[] } = JSON.parse(liveChatValues || '{}');

                    const defaultKeyExists =
                        defaultValues.Default &&
                        Array.isArray(defaultValues.Default) &&
                        defaultValues.Default.some((value: string) => value && value !== "null");
                    
                    if (defaultKeyExists) {
                        return (
                            <ButtonDialog
                                color="blue"
                                customColor={botButtonColor}
                                buttonMessage={quickReplyMessage}
                                key={index}
                                task={dialog.task}
                                fn={handleConnectAgent}
                            />
                        );
                    }
                } else if (dialog.task == 'offlineForm') {
                    return (
                        <ActionDialog
                            customColor={botButtonColor}
                            color="grey"
                            message={liveAgentInitFailMessage}
                            buttonMessage={leaveMessage}
                            key={index}
                            task={dialog.task}
                            fn={handleOfflineForm}
                        />
                    );
                } else if (dialog.task == 'forceClose' && !isVipBot) {
                    return (
                        <ActionDialog
                            color="grey"
                            message={forceClose}
                            buttonMessage={rejoinChat}
                            key={index}
                            task={dialog.task}
                            fn={handleReload}
                        />
                    );
                } else if (dialog.task == 'uploadFile') {
                    return (
                        <UploadFileDialog
                            message={uploadFile}
                            buttonMessage={t('upload')}
                            key={index}
                            task={dialog.task}
                            data={dialog.data}
                        />
                    );
                } else if (dialog.task == 'betSlip') {
                    return (
                        <BetSlipDialog
                            message={betslipId}
                            buttonMessage="Send"
                            key={index}
                            task={dialog.task}
                            data={dialog.data}
                        />
                    )
                } else if (dialog.task == 'caseDetails') {
                    return (
                        <CaseDialog
                            message="Test"
                            buttonMessage="Send"
                            key={index}
                            task={dialog.task}
                            data={dialog.data}
                        />
                    )
                }
            })}
        </div>
    );
};

Dialogs.propTypes = {};

export default Dialogs;
